import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react"

const rawBase = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL ?? "http://localhost:3015/api",
  credentials: 'include',
  prepareHeaders: async (headers) => {
    const csrfToken = sessionStorage.getItem("csrfToken")
    if (csrfToken) {
      headers.set("x-csrf-token", csrfToken)
    }
    return headers
  },
})

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extra,
) => {
  const result = await rawBase(args, api, extra)

  if (result.error?.status === 401) {
    localStorage.removeItem("adminToken")
    localStorage.removeItem("adminUser")
    sessionStorage.removeItem("csrfToken")
    window.location.href = "/login"
  }

  return result
}

export interface UserProfile {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  gender: string | null
  role: string
  isActive: boolean
  isVerified: boolean
  createdAt: string
  updatedAt: string
}

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery,
  tagTypes: ["Profile"],
  endpoints: (builder) => ({
    login: builder.mutation<{ token: string; userData: any }, { email: string; password: string }>({
      query: ({ email, password }) => ({
        url: "/auth/login",
        method: "POST",
        body: { identifier: email, password },
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          // Fetch CSRF token after successful login
          await queryFulfilled
          const csrfResponse = await fetch(`${import.meta.env.VITE_API_URL ?? "http://localhost:3015/api"}/auth/csrf-token`, {
            credentials: 'include'
          })
          const csrfData = await csrfResponse.json()
          if (csrfData.csrfToken) {
            sessionStorage.setItem("csrfToken", csrfData.csrfToken)
          }
        } catch (error) {
          console.error("Failed to fetch CSRF token:", error)
        }
      },
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          // Clear local storage and session storage
          localStorage.removeItem("adminToken")
          localStorage.removeItem("adminUser")
          sessionStorage.removeItem("csrfToken")
        } catch (error) {
          console.error("Logout failed:", error)
        }
      },
    }),
    getCsrfToken: builder.query<{ csrfToken: string }, void>({
      query: () => "/auth/csrf-token",
    }),
    getProfile: builder.query<UserProfile, void>({
      query: () => "/auth/profile",
      transformResponse: (r: any) => r.user,
      providesTags: ["Profile"],
    }),
    updateProfile: builder.mutation<void, { firstName?: string; lastName?: string; phoneNumber?: string; gender?: string }>({
      query: (body) => ({
        url: "/auth/profile",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),
    changePassword: builder.mutation<void, { currentPassword: string; newPassword: string }>({
      query: (body) => ({
        url: "/auth/change-password",
        method: "PUT",
        body,
      }),
    }),
  }),
})

export const {
  useLoginMutation,
  useLogoutMutation,
  useGetCsrfTokenQuery,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
} = authApi
